import { useState, useCallback, useEffect, useRef } from "react";
import { isFileTypeAllowed } from "../services/fileUploadService";

/**
 * Options for the useDragAndDrop hook
 */
export interface UseDragAndDropOptions {
  /** Whether drag and drop functionality is enabled */
  enabled?: boolean;
  /** Callback when files are dropped */
  onFilesDrop: (files: File[]) => void;
  /** Callback when invalid file types are dropped */
  onInvalidFileType?: (files: File[]) => void;
  /** Reference to the drop zone element */
  dropZoneRef: React.RefObject<HTMLElement | null>;
}

/**
 * Return type for the useDragAndDrop hook
 */
export interface UseDragAndDropReturn {
  /** Whether a drag is currently over the drop zone */
  isDragging: boolean;
  /** Props to spread on the drop zone element */
  dropZoneProps: {
    onDragEnter: (e: React.DragEvent) => void;
    onDragOver: (e: React.DragEvent) => void;
    onDragLeave: (e: React.DragEvent) => void;
    onDrop: (e: React.DragEvent) => void;
  };
}

/**
 * Extract files from drag event data transfer
 */
const extractFilesFromDragEvent = (dataTransfer: DataTransfer | null): File[] => {
  if (!dataTransfer) return [];
  
  const files: File[] = [];
  
  // Check for files in dataTransfer items
  if (dataTransfer.items) {
    for (let i = 0; i < dataTransfer.items.length; i++) {
      const item = dataTransfer.items[i];
      
      // Check if the item is a file
      if (item.kind === "file") {
        const file = item.getAsFile();
        if (file) {
          files.push(file);
        }
      }
    }
  }
  
  // Fallback: check dataTransfer.files directly
  if (files.length === 0 && dataTransfer.files && dataTransfer.files.length > 0) {
    for (let i = 0; i < dataTransfer.files.length; i++) {
      const file = dataTransfer.files[i];
      if (file) {
        files.push(file);
      }
    }
  }
  
  return files;
};

/**
 * Custom hook for handling drag and drop file uploads.
 * 
 * This hook provides:
 * - Drag state tracking (isDragging)
 * - Event handlers for drag/drop events
 * - File extraction and validation on drop
 * - Visual feedback support via isDragging state
 * 
 * @example
 * ```tsx
 * const { isDragging, dropZoneProps } = useDragAndDrop({
 *   enabled: !isInputDisabled,
 *   onFilesDrop: (files) => handleFileSelect(files),
 *   onInvalidFileType: (files) => toast.error('Invalid file type'),
 *   dropZoneRef: containerRef,
 * });
 * 
 * return (
 *   <div 
 *     ref={containerRef}
 *     className={isDragging ? 'drag-active' : ''}
 *     {...dropZoneProps}
 *   >
 *     Drop files here
 *   </div>
 * );
 * ```
 */
const useDragAndDrop = ({
  enabled = true,
  onFilesDrop,
  onInvalidFileType,
  dropZoneRef,
}: UseDragAndDropOptions): UseDragAndDropReturn => {
  const [isDragging, setIsDragging] = useState(false);
  
  // Track drag enter/leave count to handle nested elements
  const dragCounterRef = useRef(0);
  
  /**
   * Handle drag enter event
   */
  const handleDragEnter = useCallback(
    (e: React.DragEvent) => {
      if (!enabled) return;
      
      e.preventDefault();
      e.stopPropagation();
      
      dragCounterRef.current++;
      
      // Check if dragging files
      if (e.dataTransfer?.types?.includes("Files")) {
        setIsDragging(true);
      }
    },
    [enabled]
  );
  
  /**
   * Handle drag over event
   * Required to allow drop
   */
  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      if (!enabled) return;
      
      e.preventDefault();
      e.stopPropagation();
      
      // Set the drop effect
      if (e.dataTransfer) {
        e.dataTransfer.dropEffect = "copy";
      }
    },
    [enabled]
  );
  
  /**
   * Handle drag leave event
   */
  const handleDragLeave = useCallback(
    (e: React.DragEvent) => {
      if (!enabled) return;
      
      e.preventDefault();
      e.stopPropagation();
      
      dragCounterRef.current--;
      
      // Only set isDragging to false when we've left all elements
      if (dragCounterRef.current === 0) {
        setIsDragging(false);
      }
    },
    [enabled]
  );
  
  /**
   * Handle drop event
   */
  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      if (!enabled) return;
      
      e.preventDefault();
      e.stopPropagation();
      
      // Reset drag state
      setIsDragging(false);
      dragCounterRef.current = 0;
      
      // Extract files from drop event
      const files = extractFilesFromDragEvent(e.dataTransfer);
      
      // If no files, do nothing
      if (files.length === 0) return;
      
      // Separate valid and invalid files based on allowed file types
      const validFiles: File[] = [];
      const invalidFiles: File[] = [];
      
      files.forEach((file) => {
        if (isFileTypeAllowed(file)) {
          validFiles.push(file);
        } else {
          invalidFiles.push(file);
        }
      });
      
      // Handle invalid files
      if (invalidFiles.length > 0 && onInvalidFileType) {
        onInvalidFileType(invalidFiles);
      }
      
      // Handle valid files
      if (validFiles.length > 0) {
        onFilesDrop(validFiles);
      }
    },
    [enabled, onFilesDrop, onInvalidFileType]
  );
  
  // Reset drag state when disabled
  useEffect(() => {
    if (!enabled) {
      setIsDragging(false);
      dragCounterRef.current = 0;
    }
  }, [enabled]);
  
  // Handle drag events on document to detect when drag leaves the window
  useEffect(() => {
    if (!enabled) return;
    
    const handleDocumentDragLeave = (e: DragEvent) => {
      // Check if leaving the document/window
      if (
        e.clientX <= 0 ||
        e.clientY <= 0 ||
        e.clientX >= window.innerWidth ||
        e.clientY >= window.innerHeight
      ) {
        setIsDragging(false);
        dragCounterRef.current = 0;
      }
    };
    
    const handleDocumentDrop = () => {
      // Reset state on any document drop (in case drop happens outside our zone)
      setIsDragging(false);
      dragCounterRef.current = 0;
    };
    
    document.addEventListener("dragleave", handleDocumentDragLeave);
    document.addEventListener("drop", handleDocumentDrop);
    
    return () => {
      document.removeEventListener("dragleave", handleDocumentDragLeave);
      document.removeEventListener("drop", handleDocumentDrop);
    };
  }, [enabled]);
  
  return {
    isDragging,
    dropZoneProps: {
      onDragEnter: handleDragEnter,
      onDragOver: handleDragOver,
      onDragLeave: handleDragLeave,
      onDrop: handleDrop,
    },
  };
};

export default useDragAndDrop;

